/**
 * SEARCH-002 §3.3 / §5 — Types & helpers de routage du registre des catégories
 * de la palette ⌘K. Extrait de `searchCategories.ts` (hub), qui les ré-exporte.
 */

import { type LucideIcon } from 'lucide-react'

export type SourceType =
    | 'client'
    | 'contact'
    | 'learner'
    | 'user'
    | 'instructor'
    | 'quote'
    // 40_TENDER (mig 653) — l'appel d'offres. ⚠️ Il a passé trois jours indexé,
    // permissionné et projeté CÔTÉ SERVEUR sans exister ici : la liste de types
    // envoyée à `/search/global` est DÉRIVÉE de ce registre et ne peut que
    // restreindre (cf. `search.py`, « le param types ne peut QUE restreindre »).
    // Un type absent d'ici n'est donc jamais demandé — l'index est plein, la
    // palette et le sélecteur de fiche liée restent vides, et rien ne le signale.
    // L'en-tête de la mig 653 annonce « trois artefacts par catégorie » (fonction,
    // trigger, registre Python) : ils sont QUATRE, et c'est le quatrième qui
    // manquait.
    | 'tender'
    | 'location'
    | 'catering'
    | 'accommodation'
    | 'resource'
    | 'formation'
    | 'article'
    | 'expense'
    | 'assessment'
    | 'document'
    | 'needs_analysis'
    // Ajoutés le 2026-08-09 (mig 433). ⚠️ Les trois premiers étaient DÉJÀ émis
    // comme `linked_entity_type` par « Nouvelle tâche » (NCDetailHeader,
    // ActionDetailPage, AnswersAnalysisTab) : sans entrée ici, `linkedEntityHref`
    // renvoyait `null` et la puce « Fiche liée » de la tâche n'était PAS
    // cliquable — un lien stocké mais mort.
    | 'non_conformity'
    | 'quality_action'
    | 'satisfaction_response'
    | 'satisfaction_questionnaire'
    | 'elearning_module'
// Le SUIVI d'une fiche (43_CHK) n'est PAS un type cherchable : décision PO du
// 2026-09-30, un suivi n'est qu'un onglet (« Checklist ») de sa fiche hôte, on
// l'atteint par la fiche. L'ajouter ici le ferait demander à `/search/global`
// et réapparaître dans ⌘K comme dans « Fiche liée » — voir le gardien
// `searchCategories.test.ts`.
// (à venir, allumés par le registre quand la table arrive) :
//   'session' | 'invoice'

/** Forme renvoyée par GET /api/search/global (SEARCH-001 §6.8). */
export interface SearchHit {
    type: SourceType
    id: string
    label: string
    secondary: string | null
    matchedField: string | null
    entityId: string
    /** Routage « vers le parent » (contacts → client, ADB → client). */
    organizationId?: string | null
    /**
     * Routage document → OBJET MÉTIER d'origine (§6.11). Pour un `document`
     * rattaché/généré, type+id de l'objet à ouvrir (ex. `needs_analysis` /
     * `formation`). `null`/absent = document libre → coffre.
     */
    originType?: SourceType | null
    originId?: string | null
    /** Photo/avatar (URL publique) — affichée À LA PLACE des initiales si présente. */
    avatarUrl?: string | null
}

/**
 * Lien profond vers un objet métier d'origine (depuis un résultat `document`,
 * §6.11). Retourne `null` si le type n'est pas routable ici → repli coffre.
 * - `needs_analysis` : fiche client → onglet Activités → ADB pré-ouverte.
 * - `formation`      : fiche formation.
 * - `quote`          : fiche devis.
 * - `instructor`     : fiche intervenant.
 *
 * ⚠️ Cette fonction DOIT couvrir tous les types que `search_reindex_document`
 * sait produire dans `origin_type` — sinon le document tombe silencieusement
 * dans le repli « coffre » alors que le serveur savait où l'envoyer. C'est ce
 * qui était arrivé à `quote` (mig 405) et `instructor` (mig 406) : les deux
 * branches avaient été ajoutées côté SQL sans leur pendant ici. Un test gardien
 * relit les migrations et échoue si un nouveau type apparaît sans sa route.
 *
 * Les deux se lisent aujourd'hui sur les pièces RATTACHÉES à l'objet : le PDF
 * GÉNÉRÉ (devis, CV), lui, n'est plus un résultat distinct — il vit dans
 * l'aperçu de son parent (dédup mig 405/406).
 */
export function originRoute(
    originType: SourceType | null | undefined,
    originId: string | null | undefined,
    organizationId: string | null | undefined,
): string | null {
    if (!originType || !originId) return null
    if (originType === 'needs_analysis' && organizationId)
        return `/clients/${organizationId}?adb=${originId}#activities`
    if (originType === 'formation') return `/catalog/formations/${originId}`
    if (originType === 'quote') return `/sales/quotes/${originId}`
    if (originType === 'instructor') return `/instructors/${originId}`
    // 30_LOG / RSC-001 (mig 734) — la pièce d'un matériel : notice, facture
    // d'achat, attestation de conformité. Elle reste un résultat DISTINCT (elle
    // est déposée, pas générée), et elle mène désormais à sa fiche au lieu de
    // retomber au coffre.
    if (originType === 'resource') return `/logistics/resources/${originId}`
    return null
}

export interface CategoryConfig {
    icon: LucideIcon
    /** Navigation au clic (encapsule le routage « vers le parent », §5.3). */
    route: (hit: SearchHit) => string
    /** En-tête de groupe : t(`globalSearch.group.${i18nKey}`). */
    i18nKey: string
    /** Slug RBAC requis pour requêter/afficher ce type (defense-in-depth, §9). */
    permission: string
    /**
     * Slugs ALTERNATIFS qui ouvrent le type sans le read large — miroir exact de
     * `TYPE_PERMISSION_ALT` (`backend/src/infra/search_repository/registry.py`).
     *
     * ⚠️ **Sans eux, le front ampute AVANT l'appel ce que le serveur autoriserait.**
     * La liste `types` envoyée à `/search/global` est dérivée de ce registre et ne
     * peut que RESTREINDRE : un porteur de `clients:own_only` seul (ou de
     * `clients:read_team`) ne demandait donc AUCUN client — ni dans ⌘K, ni dans
     * « Relier une fiche » — alors que la liste des clients, elle, lui en montre.
     * Le serveur borne ensuite les LIGNES au propriétaire (ou à l'équipe) : ouvrir
     * le type ici ne fait fuir personne, c'est la requête qui gate.
     */
    permissionAlt?: readonly string[]
    /** Ordre stable d'affichage (jamais par nombre de hits, §4.1). */
    order: number
    /** Rendu d'aperçu (générique piloté par l'API, ou spécifique document). */
    previewKind: 'generic' | 'document'
    /**
     * « Voir tout » (§4.8) : construit le deep-link vers le DataTable du module
     * préfiltré. `null` ⇒ pas de listing filtrable ⇒ overflow non affiché.
     */
    listing: {
        toRoute: (q: string, matchedField: string | null) => string
    } | null
}

/** Encode le préfiltre `?q=` + `matchedField` pour un listing module. */
export function listingRoute(base: string) {
    return (q: string, matchedField: string | null): string => {
        const params = new URLSearchParams({ q })
        if (matchedField) params.set('matched_field', matchedField)
        return `${base}?${params.toString()}`
    }
}
